import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@services/api-client';

export type PreChatFieldMode = 'required' | 'optional' | 'hidden';
export type WidgetTone = 'friendly' | 'professional' | 'concise';

/**
 * Which languages this tenant's chatbot answers in. Mirrors the gateway's
 * ChatWidgetLanguagesDto; the picker itself is built from CHAT_LANGUAGES so
 * there is only ever one list.
 */
export interface ChatWidgetLanguages {
  enabled?: boolean;
  supported?: string[];
  default?: string;
}

/** Appearance, behaviour and bot identity (mirrors channel-gateway's ChatWidgetSettingsDto). */
export interface ChatWidgetSettings {
  botName?: string;
  companyName?: string;
  tone?: WidgetTone;
  welcomeMessage?: string;
  offlineMessage?: string;
  primaryColor?: string;
  position?: 'bottom-right' | 'bottom-left';
  launcherText?: string;
  avatarUrl?: string;
  autoOpenDelaySeconds?: number;
  preChatFields?: Partial<Record<'name' | 'email' | 'phone', PreChatFieldMode>>;
  languages?: ChatWidgetLanguages;
}

export interface ChatWidgetConfig {
  id: string;
  publicKey: string;
  enabled: boolean;
  allowedOrigins: string[];
  settings: ChatWidgetSettings;
  lastSeenOrigin: string | null;
  lastSeenAt: string | null;
  keyRotatedAt: string | null;
  updatedAt: string;
  embed: { loaderUrl: string; hostedChatUrl: string; apiUrl: string | null } | null;
  /** The languages an admin may choose from, sent by the server. */
  languageOptions?: Array<{ code: string; name: string; nativeName: string }>;
}

export interface UpdateChatWidgetConfig {
  enabled?: boolean;
  allowedOrigins?: string[];
  settings?: ChatWidgetSettings;
}

const BASE = '/chat/widget-admin/config';

export const chatWidgetApi = {
  get: async (): Promise<ChatWidgetConfig> => (await apiClient.get<ChatWidgetConfig>(BASE)).data,
  update: async (changes: UpdateChatWidgetConfig): Promise<ChatWidgetConfig> =>
    (await apiClient.put<ChatWidgetConfig>(BASE, changes)).data,
  rotateKey: async (): Promise<ChatWidgetConfig> =>
    (await apiClient.post<ChatWidgetConfig>(`${BASE}/rotate-key`)).data,
};

export const chatWidgetKeys = {
  all: ['chat-widget'] as const,
  config: (tenantId?: string) => [...chatWidgetKeys.all, 'config', tenantId] as const,
};

export function useChatWidgetConfig(tenantId?: string) {
  return useQuery({
    queryKey: chatWidgetKeys.config(tenantId),
    queryFn: chatWidgetApi.get,
    enabled: !!tenantId,
  });
}

export function useUpdateChatWidgetConfig(tenantId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: chatWidgetApi.update,
    onSuccess: (config) => queryClient.setQueryData(chatWidgetKeys.config(tenantId), config),
  });
}

export function useRotateChatWidgetKey(tenantId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: chatWidgetApi.rotateKey,
    onSuccess: (config) => queryClient.setQueryData(chatWidgetKeys.config(tenantId), config),
  });
}

/** The server's message for a failed request, for a toast. */
export function errorMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return typeof message === 'string' && message ? message : fallback;
}
